/** Capture de la page visible pour le signalement de bug (sans la modale). */

import type { Options } from 'html2canvas';

const MAX_EDGE_PX = 1920;
const JPEG_QUALITY = 0.82;
export const BUG_REPORT_SCREENSHOT_MAX_BYTES = 5 * 1024 * 1024;

const CAPTURE_UI_SELECTOR = '[data-hide-for-bug-report-capture]';

export type BugReportCaptureHideSession = {
  restore: () => void;
};

type CropTargetFactory = {
  fromElement(element: Element): Promise<unknown>;
};

type CroppableMediaTrack = MediaStreamTrack & {
  cropTo?(target: unknown): Promise<void>;
};

/** Masque modale, bulle support, etc. avant toute capture (html2canvas ou getDisplayMedia). */
export function beginBugReportCaptureHide(): BugReportCaptureHideSession {
  const hidden: Array<{ el: HTMLElement; display: string; visibility: string }> = [];
  document.querySelectorAll(CAPTURE_UI_SELECTOR).forEach((node) => {
    const el = node as HTMLElement;
    hidden.push({ el, display: el.style.display, visibility: el.style.visibility });
    el.style.display = 'none';
    el.style.visibility = 'hidden';
  });

  const previousOverflow = document.body.style.overflow;
  document.body.style.overflow = 'visible';

  return {
    restore: () => {
      document.body.style.overflow = previousOverflow;
      hidden.forEach(({ el, display, visibility }) => {
        el.style.display = display;
        el.style.visibility = visibility;
      });
    },
  };
}

/** Attend le prochain rendu après masquage UI (React + peinture navigateur). */
export async function waitForBugReportCapturePaint(extraMs = 120): Promise<void> {
  await new Promise<void>((resolve) => {
    setTimeout(() => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    }, extraMs);
  });
}

function makeScreenshotFilename(): string {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
  return `bug-report-${timestamp}.jpg`;
}

const CAPTURE_IGNORE_TAGS = new Set([
  'IFRAME',
  'VIDEO',
  'OBJECT',
  'EMBED',
  'CANVAS',
  'SCRIPT',
  'NOSCRIPT',
]);

function viewportMetrics(): {
  cssWidth: number;
  cssHeight: number;
  offsetX: number;
  offsetY: number;
  dpr: number;
} {
  const vv = window.visualViewport;
  return {
    cssWidth: vv?.width ?? window.innerWidth,
    cssHeight: vv?.height ?? window.innerHeight,
    offsetX: vv?.offsetLeft ?? 0,
    offsetY: vv?.offsetTop ?? 0,
    dpr: window.devicePixelRatio || 1,
  };
}

function downscaleCanvas(source: HTMLCanvasElement, maxEdge: number): HTMLCanvasElement {
  const w = source.width;
  const h = source.height;
  if (w <= 0 || h <= 0) throw new Error('Canvas vide');
  const scale = Math.min(1, maxEdge / Math.max(w, h));
  if (scale >= 1) return source;
  const out = document.createElement('canvas');
  out.width = Math.round(w * scale);
  out.height = Math.round(h * scale);
  const ctx = out.getContext('2d');
  if (!ctx) return source;
  ctx.drawImage(source, 0, 0, out.width, out.height);
  return out;
}

function dataUrlToFile(dataUrl: string, filename: string): File {
  const comma = dataUrl.indexOf(',');
  if (comma < 0) throw new Error('Capture impossible');
  const header = dataUrl.slice(0, comma);
  const base64 = dataUrl.slice(comma + 1);
  const mimeMatch = header.match(/data:([^;]+)/);
  const mime = mimeMatch?.[1] ?? 'image/jpeg';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new File([bytes], filename, { type: mime, lastModified: Date.now() });
}

function canvasToJpegFile(
  canvas: HTMLCanvasElement,
  filename: string,
  quality = JPEG_QUALITY,
): Promise<File> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(new File([blob], filename, { type: 'image/jpeg', lastModified: Date.now() }));
          return;
        }
        try {
          resolve(dataUrlToFile(canvas.toDataURL('image/jpeg', quality), filename));
        } catch (err) {
          reject(err instanceof Error ? err : new Error('Capture impossible'));
        }
      },
      'image/jpeg',
      quality,
    );
  });
}

async function compressToMaxBytes(canvas: HTMLCanvasElement, filename: string): Promise<File> {
  let quality = JPEG_QUALITY;
  let scaled = downscaleCanvas(canvas, MAX_EDGE_PX);
  for (let attempt = 0; attempt < 8; attempt += 1) {
    const file = await canvasToJpegFile(scaled, filename, quality);
    if (file.size <= BUG_REPORT_SCREENSHOT_MAX_BYTES) return file;
    quality = Math.max(0.45, quality - 0.08);
    scaled = downscaleCanvas(scaled, Math.round(MAX_EDGE_PX * 0.85));
  }
  return canvasToJpegFile(scaled, filename, quality);
}

function sanitizeClonedDocument(clonedDoc: Document): void {
  clonedDoc.querySelectorAll('[data-hide-for-bug-report-capture]').forEach((node) => {
    node.remove();
  });
  clonedDoc.querySelectorAll('iframe, video, object, embed, script, noscript').forEach((node) => {
    node.remove();
  });
  clonedDoc.querySelectorAll<HTMLElement>('*').forEach((el) => {
    el.style.backdropFilter = 'none';
    el.style.setProperty('-webkit-backdrop-filter', 'none');
  });
}

function buildCaptureOptions(overrides: Partial<Options> = {}): Partial<Options> {
  const { cssWidth, cssHeight, dpr } = viewportMetrics();
  return {
    useCORS: true,
    allowTaint: true,
    backgroundColor: window.getComputedStyle(document.body).backgroundColor || '#ffffff',
    scale: dpr,
    logging: false,
    imageTimeout: 1500,
    ignoreElements: (el) => {
      if (CAPTURE_IGNORE_TAGS.has(el.tagName)) return true;
      if (el.closest('[data-hide-for-bug-report-capture]')) return true;
      return false;
    },
    onclone: (_doc, element) => {
      sanitizeClonedDocument(element.ownerDocument ?? document);
    },
    width: cssWidth,
    height: cssHeight,
    windowWidth: cssWidth,
    windowHeight: cssHeight,
    scrollX: 0,
    scrollY: 0,
    x: window.scrollX,
    y: window.scrollY,
    ...overrides,
  };
}

async function renderWithHtml2canvas(
  target: HTMLElement,
  options: Partial<Options>,
): Promise<HTMLCanvasElement> {
  const { default: html2canvas } = await import('html2canvas');
  const canvas = await html2canvas(target, options);
  if (canvas.width <= 0 || canvas.height <= 0) {
    throw new Error('Canvas vide');
  }
  return canvas;
}

async function captureWithHtml2canvas(): Promise<HTMLCanvasElement> {
  const target = document.documentElement;
  return renderWithHtml2canvas(target, buildCaptureOptions());
}

async function waitForStableVideoFrame(video: HTMLVideoElement): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const timeout = window.setTimeout(() => reject(new Error('Frame vidéo indisponible')), 5000);
    const done = () => {
      window.clearTimeout(timeout);
      resolve();
    };
    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && video.videoWidth > 0) {
      done();
      return;
    }
    video.onloadeddata = done;
    video.onerror = () => {
      window.clearTimeout(timeout);
      reject(new Error('Frame vidéo indisponible'));
    };
  });

  // Laisser le flux se stabiliser (évite une frame partielle / floue).
  await new Promise<void>((resolve) => {
    if ('requestVideoFrameCallback' in video) {
      (video as HTMLVideoElement & { requestVideoFrameCallback(cb: () => void): void })
        .requestVideoFrameCallback(() => resolve());
      return;
    }
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

async function tryCropTrackToViewport(track: MediaStreamTrack): Promise<void> {
  const CropTarget = (window as Window & { CropTarget?: CropTargetFactory }).CropTarget;
  if (!CropTarget?.fromElement) return;

  const target = document.getElementById('root') ?? document.body;
  try {
    const cropTarget = await CropTarget.fromElement(target);
    const croppable = track as CroppableMediaTrack;
    if (typeof croppable.cropTo === 'function') {
      await croppable.cropTo(cropTarget);
    }
  } catch {
    // CropTarget optionnel (Chrome / Edge récents).
  }
}

/** Extrait la zone viewport visible depuis une frame getDisplayMedia. */
function canvasFromVideoViewport(video: HTMLVideoElement): HTMLCanvasElement {
  const { cssWidth, cssHeight, offsetX, offsetY, dpr } = viewportMetrics();
  const outW = Math.max(1, Math.round(cssWidth * dpr));
  const outH = Math.max(1, Math.round(cssHeight * dpr));

  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas indisponible');

  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (vw <= 0 || vh <= 0) throw new Error('Frame vidéo vide');

  // Ratio pixels vidéo / pixels CSS (onglet ou écran partagé).
  const pxPerCssX = vw / cssWidth;
  const pxPerCssY = vh / cssHeight;

  const sx = Math.max(0, Math.round(offsetX * pxPerCssX));
  const sy = Math.max(0, Math.round(offsetY * pxPerCssY));
  const sw = Math.min(vw - sx, Math.round(cssWidth * pxPerCssX));
  const sh = Math.min(vh - sy, Math.round(cssHeight * pxPerCssY));

  ctx.drawImage(video, sx, sy, sw, sh, 0, 0, outW, outH);
  return canvas;
}

/**
 * Capture native de l’onglet (prioritaire) : pixels réels, fidèle à l’affichage.
 * L’utilisateur choisit « Cet onglet » dans la fenêtre du navigateur.
 */
export async function captureBugReportScreenshotViaDisplayMedia(): Promise<File> {
  const md = navigator.mediaDevices;
  if (!md?.getDisplayMedia) {
    throw new Error('Capture native indisponible');
  }

  await waitForBugReportCapturePaint(80);

  const { cssWidth, cssHeight, dpr } = viewportMetrics();
  const stream = await md.getDisplayMedia({
    video: {
      displaySurface: 'browser',
      width: { ideal: Math.round(cssWidth * dpr) },
      height: { ideal: Math.round(cssHeight * dpr) },
    },
    audio: false,
    preferCurrentTab: true,
    selfBrowserSurface: 'include',
  } as DisplayMediaStreamOptions);

  const [track] = stream.getVideoTracks();
  if (track) {
    await tryCropTrackToViewport(track);
  }

  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.srcObject = stream;

  try {
    await video.play();
    await waitForStableVideoFrame(video);
    const canvas = canvasFromVideoViewport(video);
    return compressToMaxBytes(canvas, makeScreenshotFilename());
  } finally {
    stream.getTracks().forEach((t) => t.stop());
    video.srcObject = null;
  }
}

/**
 * Repli html2canvas (CSS moderne parfois incompatible).
 * Appeler beginBugReportCaptureHide() avant.
 */
export async function captureBugReportScreenshot(): Promise<File> {
  const raw = await captureWithHtml2canvas();
  return compressToMaxBytes(raw, makeScreenshotFilename());
}

export function readClipboardImageFile(event: ClipboardEvent): File | null {
  const items = event.clipboardData?.items;
  if (items) {
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const blob = item.getAsFile();
        if (blob) {
          const ext = blob.type.split('/')[1] || 'png';
          return new File([blob], `bug-report-paste.${ext}`, {
            type: blob.type,
            lastModified: Date.now(),
          });
        }
      }
    }
  }

  const files = event.clipboardData?.files;
  if (files?.length) {
    const file = files[0];
    if (file.type.startsWith('image/')) return file;
  }

  return null;
}
